import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Activity, Building2, Users, ScrollText, LogOut, ArrowLeft, ShieldCheck, Rocket, Radio, MessageCircle } from "lucide-react";

const NAV = [
  { to: "/platform", label: "Overview", icon: Activity, end: true, testId: "platform-nav-overview" },
  { to: "/platform/companies", label: "Companies", icon: Building2, testId: "platform-nav-companies" },
  { to: "/platform/onboarding", label: "Client Onboarding", icon: Rocket, testId: "platform-nav-onboarding" },
  { to: "/platform/events", label: "Comms Activity", icon: Radio, testId: "platform-nav-events" },
  { to: "/platform/users", label: "Platform Users", icon: Users, testId: "platform-nav-users" },
  { to: "/platform/audit", label: "Audit Log", icon: ScrollText, testId: "platform-nav-audit" },
  { to: "/platform/whatsapp", label: "WhatsApp", icon: MessageCircle, testId: "platform-nav-whatsapp" },
  { to: "/platform/data-requests", label: "Data Requests", icon: ScrollText, testId: "platform-nav-data-requests" },
];

const linkClass = ({ isActive }) =>
  [
    "flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150",
    isActive
      ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30"
      : "text-slate-400 hover:bg-white/5 hover:text-slate-100",
  ].join(" ");

export default function PlatformLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100" data-testid="platform-shell">
      <aside className="w-64 shrink-0 bg-slate-900/80 border-r border-white/5 flex flex-col">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/5">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
            <ShieldCheck className="w-[18px] h-[18px] text-slate-950" strokeWidth={2.5} />
          </span>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-tight">OperixAI</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400/80">Platform Admin</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} data-testid={n.testId}>
              <n.icon className="w-[18px] h-[18px]" strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/5 space-y-1">
          <button
            onClick={() => navigate("/")}
            data-testid="platform-back-to-app"
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="w-[18px] h-[18px]" /> Back to app
          </button>
          <button
            onClick={logout}
            data-testid="platform-logout"
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-rose-400 hover:bg-rose-500/10 transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" /> Sign out
          </button>
          <div className="px-1 pt-2 text-[11px] text-slate-600 truncate">{user?.email}</div>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
